import { useCallback, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import HelperInputText from "../text/HelperInputText";
import { ControlledTextField } from "./ControlledTextField";
import { getCouponFieldStyle } from "../Checkout/CouponStyles";
import { useTheme } from "../../util/themes";
import { OrderDeliveryAddressFields } from "../../types/purchaseOrder/Order";
import { CHECKOUT_MAXLEN } from "../../utils/checkoutFieldMaxLengths";
import { HttpCache } from "../../http";

let placesLibPromise: Promise<google.maps.PlacesLibrary> | null = null;

const ALIAS_PROVINCIA: Record<string, string> = {
    "ciudad autonoma de buenos aires": "caba",
    "ciudad de buenos aires": "caba",
};

function normalizarProvincia(s: string): string {
    const lower = s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\./g, "").trim();
    if (ALIAS_PROVINCIA[lower]) return ALIAS_PROVINCIA[lower];
    return lower.replace(/^(provincia de |departamento de )/i, "").trim();
}

function loadPlacesLib(): Promise<google.maps.PlacesLibrary> | null {
    const key = (window as any).GOOGLE_MAPS_KEY as string | undefined;
    if (!key) return null;
    if (!placesLibPromise) {
        setOptions({ key, version: "weekly" });
        placesLibPromise = importLibrary("places") as Promise<google.maps.PlacesLibrary>;
    }
    return placesLibPromise;
}

interface PlaceSuggestion {
    label: string;
    mainText: string;
    secondaryText: string;
    prediction: google.maps.places.PlacePrediction;
}

interface GooglePlacesStreetFieldProps {
    control: any;
    name: string;
    baseName: string;
    setValue: (name: string, value: any, options?: object) => void;
}

export const GooglePlacesStreetField: React.FC<GooglePlacesStreetFieldProps> = ({
    control,
    name,
    baseName,
    setValue,
}) => {
    const theme = useTheme();
    const couponFieldStyle = getCouponFieldStyle(theme as any);
    const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [placesUnavailable, setPlacesUnavailable] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchSuggestions = useCallback((input: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (input.length < 3) {
            setSuggestions([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            let lib: google.maps.PlacesLibrary | null = null;
            try {
                lib = await loadPlacesLib();
            } catch {
                setPlacesUnavailable(true);
                return;
            }
            if (!lib) return;

            const AutocompleteSuggestion = (lib as any).AutocompleteSuggestion;
            if (!AutocompleteSuggestion) {
                setPlacesUnavailable(true);
                return;
            }

            setLoading(true);
            try {
                const { suggestions: results } =
                    await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                        input,
                        includedRegionCodes: ["ar"],
                    });

                setSuggestions(
                    results.map((s: any) => ({
                        label: s.placePrediction.text.text as string,
                        mainText:
                            s.placePrediction.structuredFormat?.mainText?.text ??
                            s.placePrediction.text.text,
                        secondaryText:
                            s.placePrediction.structuredFormat?.secondaryText?.text ?? "",
                        prediction: s.placePrediction,
                    })),
                );
            } catch (e) {
                console.error("[GooglePlaces]", e);
                setSuggestions([]);
            }
            setLoading(false);
        }, 350);
    }, []);

    const handleSelect = useCallback(
        async (suggestion: PlaceSuggestion) => {
            try {
                const place = suggestion.prediction.toPlace();
                await place.fetchFields({ fields: ["addressComponents"] });

                const get = (type: string) =>
                    place.addressComponents?.find((c: any) => c.types.includes(type))?.longText ??
                    "";

                const opts = { shouldValidate: true, shouldDirty: true };

                const street = get("route");
                const streetNumber = get("street_number");
                const locality =
                    get("locality") ||
                    get("sublocality_level_1") ||
                    get("administrative_area_level_2");
                const googleProvincia = get("administrative_area_level_1");

                // No seteamos ZipCode: el CP ya viene del Paso 1 y cambiarlo dispara
                // un useEffect en useCheckoutForm que resetea toda la dirección.

                if (street)
                    setValue(`${baseName}.${OrderDeliveryAddressFields.Street}`, street, opts);
                if (streetNumber)
                    setValue(
                        `${baseName}.${OrderDeliveryAddressFields.StreetNumber}`,
                        streetNumber,
                        opts,
                    );
                if (locality)
                    setValue(
                        `${baseName}.${OrderDeliveryAddressFields.LocalityDesc}`,
                        locality,
                        opts,
                    );

                if (googleProvincia) {
                    try {
                        const provincias = await HttpCache.getProvincies();
                        const normalizada = normalizarProvincia(googleProvincia);
                        const match = provincias.find(
                            (p) => normalizarProvincia(p.descripcion) === normalizada,
                        );
                        if (match)
                            setValue(
                                `${baseName}.${OrderDeliveryAddressFields.ProvinceCode}`,
                                match.id,
                                opts,
                            );
                    } catch {}
                }
            } catch {}
        },
        [baseName, setValue],
    );

    if (placesUnavailable) {
        return (
            <ControlledTextField
                control={control}
                label="Calle"
                name={name}
                required
                inputProps={{ maxLength: CHECKOUT_MAXLEN.vchCalle }}
            />
        );
    }

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Stack sx={{ width: "100%" }}>
                    <Autocomplete
                        freeSolo
                        autoHighlight
                        options={suggestions}
                        loading={loading}
                        inputValue={value ?? ""}
                        onInputChange={(_, newVal, reason) => {
                            if (reason === "reset") return;
                            onChange(newVal);
                            fetchSuggestions(newVal);
                        }}
                        onChange={(_, selected) => {
                            if (selected && typeof selected !== "string") {
                                handleSelect(selected);
                            }
                        }}
                        getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
                        filterOptions={(x) => x}
                        renderOption={(props, option) => {
                            const { key, ...liProps } = props as any;
                            return (
                                <Box
                                    key={key}
                                    component="li"
                                    {...liProps}
                                    sx={{
                                        alignItems: "flex-start !important",
                                        gap: 1.5,
                                        py: "10px !important",
                                        px: "14px !important",
                                    }}
                                >
                                    <LocationOnIcon
                                        sx={{
                                            color: "text.secondary",
                                            flexShrink: 0,
                                            mt: 0.2,
                                            fontSize: 18,
                                        }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, lineHeight: 1.35 }}
                                        >
                                            {option.mainText}
                                        </Typography>
                                        {option.secondaryText && (
                                            <Typography
                                                variant="caption"
                                                sx={{ color: "text.secondary", lineHeight: 1.25 }}
                                            >
                                                {option.secondaryText}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                sx={couponFieldStyle}
                                label={
                                    <Stack direction="row" spacing={0.4}>
                                        <div>Calle</div>
                                        <Typography>*</Typography>
                                    </Stack>
                                }
                                variant="outlined"
                                error={!!error}
                                inputProps={{
                                    ...params.inputProps,
                                    maxLength: CHECKOUT_MAXLEN.vchCalle,
                                }}
                                InputLabelProps={{
                                    shrink: Boolean(value),
                                }}
                            />
                        )}
                    />
                    {error && <HelperInputText text={error.message} error />}
                </Stack>
            )}
        />
    );
};
